<template>
    <AppModal
        ref="modalRef"
        id="connection_history_modal"
        :model-value="modelValue"
        @update:modelValue="onCloseModal"
        size="xl"
        :containerStyles="{
            'max-width': 'fit-content',
            width: '90%'
        }"
        :scrollable="true">
        <template #header>
            <h2 class="modal-title pb-2 me-4" id="myExtraLargeModalLabel">{{ $t("Connection History") }}</h2>
            <div class="d-flex">
                <!--                <div class="mx-2"><strong>ICCID: {{ iccid || '&#45;&#45;' }}</strong></div>-->
                <div class="mx-2">
                    <strong>{{ $t("Cellular Network Status") }}</strong>
                </div>
                <font-awesome-icon
                    :icon="faRefresh"
                    @click="refreshData"
                    class="me-2"
                    :class="{ rotating: isRefreshing }"
                    style="margin-top: 3px"
                />
                <div class="mx-2">
                    <div>
                        <strong>{{ $t("Last Session Start Time") }}</strong>
                    </div>
                    <div>{{ convertAndFormatTimestamp(dataSession?.lastStartTime) || "--" }}</div>
                </div>
                <div class="mx-2">
                    <div>
                        <strong>{{ $t("Last Session End Time") }}</strong>
                    </div>
                    <div>{{ convertAndFormatTimestamp(dataSession?.lastStopTime) || "--" }}</div>
                </div>
            </div>
        </template>
        <template #default>
            <table class="table table-hover table-bordered table-striped table-w-auto text-nowrap text-center">
                <thead>
                    <tr>
                        <th>
                            <b>{{ $t("Start Date") }}</b>
                        </th>
                        <th>
                            <b>{{ $t("End Date") }}</b>
                        </th>
                        <th>
                            <b>{{ $t("Connection Time") }}</b>
                        </th>
                        <th>
                            <b>{{ $t("Status") }}</b>
                        </th>
                        <th>
                            <b>{{ $t("Connection Type") }}</b>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="history in connectionHistory.data"
                        :key="history.end_time"
                        :class="{ 'blurred-row': isLoading }"
                    >
                        <td>{{ format(history.start_time, "MM/DD/YYYY hh:mm A") }}</td>
                        <td>{{ format(history.end_time, "MM/DD/YYYY hh:mm A") }}</td>
                        <td>{{ connection_time(history.start_time, history.end_time) }}</td>
                        <td v-html="formatIsSuccess(history.exit_flag)"></td>
                        <td>
                            <template v-if="connectionType === 1">
                                {{ connection_type(history.terminal_type) }}
                            </template>
                            <template v-else>
                                {{ history.connection_type }}
                            </template>
                        </td>
                    </tr>
                </tbody>
            </table>
            <AppLoading :loading="isLoading" />
        </template>
        <template #footer>
            <div class="row justify-content-between align-items-center">
                <!-- Right column for pagination controls -->
                <div class="col-auto">
                    <div class="row justify-content-between align-items-center">
                        <div class="col-md-6">
                            {{ $t("Showing") }} {{ connectionHistory.from || 0 }} {{ $t("to") }}
                            {{ connectionHistory.to || 0 }}
                        </div>

                        <div class="col-md-6 mt-3 mt-md-0">
                            <ul class="pagination justify-content-end">
                                <li class="page-item pagination-prev-nav" v-if="connectionHistory.prev_page_url">
                                    <a class="page-link" href="#" aria-label="Previous" @click="changePage(page - 1)">
                                        <span aria-hidden="true">&laquo; Prev</span>
                                    </a>
                                </li>

                                <li
                                    class="page-item pagination-next-nav"
                                    v-if="connectionHistory.next_page_url || connectionHistory.next_token"
                                >
                                    <a class="page-link" href="#" aria-label="Next" @click="changePage(page + 1)">
                                        <span aria-hidden="true">Next &raquo;</span>
                                    </a>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </AppModal>
</template>

<script setup>
import AppModal from "../../shared/AppModal.vue";
import { Bootstrap5Pagination as Pagination } from "laravel-vue-pagination";
import { computed, onMounted, ref, watch } from "vue";
import oldDates from "@/vue/composables/useDates";
import { formatIsSuccess } from "@/vue/helpers";
import AppLoading from "@/vue/shared/AppLoading.vue";
import { faRefresh } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { getPatientDevicesConnectionHistories } from "@/api-RH";
import { trans as $t } from "@/vue/composables/missingTranslations";
import useDates from "@/libs/dates/useDates";
const emits = defineEmits(["closeModal"]);

const props = defineProps({
    modelValue: {
        type: Boolean,
        default: false
    },
    machine: {
        type: Object
    },
    connectionType: {
        type: Number,
        default: 1
    },
    last_connection_history: {
        type: Object,
        required: true
    }
});

const { getFormattedDate } = oldDates("MM/DD/YYYY hh:mm A");
const { format } = useDates();
const modelValue = ref(false);

const formatIP = (num) => {
    return ((num >>> 24) & 0xff) + "." + ((num >>> 16) & 0xff) + "." + ((num >>> 8) & 0xff) + "." + (num & 0xff);
};

const iccid = ref(null);
const networkDetails = ref(null);
const dataSession = ref(null);

const refreshData = async () => {
    try {
        if (props.machine) {
            isRefreshing.value = true;
            let serial = props.machine.serial ?? props.machine.device_serial;
            const response = await axios.get(`/api/machines/${serial}/iccid-data`);
            iccid.value = response.data.iccid;

            if (iccid.value && iccid.value !== "") {
                const responseDeviceDetails = await axios.get(`/api/aeris/${iccid.value}/device-details`);
                networkDetails.value = responseDeviceDetails.data.networkResponse[0] || {};
                dataSession.value = networkDetails.value.dataSession;
            }

            isRefreshing.value = false;
        }
    } catch (error) {
        isRefreshing.value = false;
        console.error(error);
    }
};

const convertAndFormatTimestamp = (timestamp) => {
    if (!timestamp || isNaN(new Date(timestamp).getTime())) {
        return "";
    }
    const date = new Date(timestamp);

    return new Intl.DateTimeFormat("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        timeZoneName: "short"
    }).format(date);
};

const connection_time = function (start_time, end_time) {
    const startTime = new Date(start_time);
    const endTime = new Date(end_time);
    const timeDifferenceInMinutes = (endTime - startTime) / (1000 * 60);
    const formattedTimeDifference = timeDifferenceInMinutes.toFixed(1);
    const displayTime = formattedTimeDifference === "0.0" ? "0" : formattedTimeDifference;

    return `${displayTime} ${$t("minutes")}`;
};

const connection_type = function (terminal_type) {
    return terminal_type === 1
        ? $t("Wireless")
        : terminal_type === 2
        ? $t("App")
        : terminal_type === 3
        ? $t("Pc")
        : $t("Unknown");
};

const isLoading = ref(false);
const page = ref(1);
const pageLength = ref(10);
const connectionHistory = ref([]);
const modalRef = ref(null);
const isRefreshing = ref(false);

const onCloseModal = (state) => {
    if (!state) {
        modelValue.value = false;
        emits("closeModal");
    }
};

const url = computed(() => {
    if (props.connectionType === 1) {
        //return `/api/patients/${props.machine.patient_id}/devices/${props.machine.history_id}/connection_history?`;
        const { patient_id, history_id } = props.machine;
        return getPatientDevicesConnectionHistories.route({ patient_id, history_id }) + "?";
    }
    return `/api/machines/${props.machine.guid}/connection-history?orderBy=end_time&orderDir=desc&`;
});

const fetchData = async function () {
    try {
        isLoading.value = true;

        const params = new URLSearchParams();
        params.append("page", page.value);
        params.append("limit", pageLength.value);

        await axios.get(`${url.value}${params}`).then((response) => {
            connectionHistory.value = response.data;
        });
    } catch (error) {
        console.error(error);
    } finally {
        isLoading.value = false;
    }
};

const changePage = async function (newPage) {
    page.value = newPage;
    await fetchData();
};

const closeModal = () => {
    modalRef.value.closeModal();
};

watch(
    () => props.modelValue,
    (newValue) => {
        modelValue.value = newValue;
        if (!newValue) {
            return;
        }
        fetchData();
        refreshData();
    }
);

onMounted(() => {
    if (props.modelValue) {
        fetchData();
        refreshData();
    }
});
</script>

<style scoped>
.loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1;
}

.blurred-row {
    filter: blur(5px);
}
@keyframes rotate {
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
}
.rotating {
    animation: rotate 1s linear infinite;
}
</style>
